import { useTranslation } from 'react-i18next';
import { formatQuantityForUnit } from '~/utils';
import type { OrderForBuilder } from '~/api/order';
import {
  computeSourcingCoverage,
  type SourcingCoverageRow,
} from '~/pages/order-builder/sourcingCoverage';
import type { Cart } from '~/graphql/graphql';

type SourcingOrderItemsProps = {
  order: OrderForBuilder;
  carts: Cart[];
  activeLengthId: string;
  onSelectItem: (item: { productId: string; lengthId: string }) => void;
};

function rowStatusLabel(
  row: SourcingCoverageRow,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const covered = formatQuantityForUnit({ value: row.covered, unit: row.unit });
  const required = formatQuantityForUnit({
    value: row.required,
    unit: row.unit,
  });
  if (row.isCovered) {
    return t('order_builder.sourcing.item_covered', { covered, required });
  }
  const remaining = formatQuantityForUnit({
    value: Math.max(0, row.required - row.covered),
    unit: row.unit,
  });
  return t('order_builder.sourcing.item_remaining', {
    covered,
    required,
    remaining,
  });
}

export function SourcingOrderItems({
  order,
  carts,
  activeLengthId,
  onSelectItem,
}: Readonly<SourcingOrderItemsProps>) {
  const { t } = useTranslation('order-builder');

  const coverageRows = computeSourcingCoverage(order, carts);

  return (
    <div
      className="bg-neut-light rounded-lg border border-neut-border shadow-neut-100 p-4 sm:p-6"
      data-testid="sourcing-order-items"
    >
      <div className="text-xs font-bold uppercase tracking-wide text-neut-400 mb-3">
        {t('order_builder.sourcing.items_title')}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        {coverageRows.map((row) => {
          const isActive = row.platformProductsLengthId === activeLengthId;
          const isFullyCovered = row.isCovered;
          return (
            <button
              key={row.platformProductsLengthId}
              type="button"
              data-testid="sourcing-order-item-row"
              onClick={() =>
                onSelectItem({
                  productId: row.platformProductId,
                  lengthId: row.platformProductsLengthId,
                })
              }
              className={`flex-1 text-left rounded-lg border p-3 transition-colors cursor-pointer ${
                isActive
                  ? 'border-2 border-success-700 bg-success-50'
                  : 'border-neut-border bg-neut-light hover:bg-neut-50'
              }`}
            >
              <div className="font-semibold text-sm text-neut-prim">
                {row.productName}{' '}
                <span className="px-2 py-1 bg-neut-100 rounded-md text-xs font-normal">
                  {row.lengthDisplayValue}
                </span>{' '}
                {isActive && (
                  <span className="text-xs font-normal text-success-700">
                    {t('order_builder.sourcing.active')}
                  </span>
                )}
              </div>
              <div
                className={`text-xs font-semibold ${
                  isFullyCovered ? 'text-success-600' : 'text-amber-600'
                }`}
              >
                {rowStatusLabel(row, t)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
